import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getManagementTenantPreferencesQueryKey,
  updateTenantMutation,
  type TenantRead,
} from "@harmonyso/tenants-sdk";
import { cellRequestOptions } from "@/lib/cells/cells";
import { invalidateCellTenantQueries } from "@/lib/cells/cell-queries";
import * as z from "zod";
import { Edit, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { TrialDateFields } from "@/components/tenants/trial-date-fields";
import {
  checkTrialDates,
  fromDateInputValue,
  toDateInputValue,
  trialDateFormFields,
} from "@/lib/trial";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Helper function to extract error message from unknown error
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } })
      .response;
    if (response?.data?.detail) {
      return response.data.detail;
    }
  }
  if (error && typeof error === "object" && "message" in error) {
    return (error as { message: string }).message;
  }
  return "Unknown error occurred";
};

const tenantFormSchema = z
  .object({
    company: z.string().min(1, "Company name is required"),
    subdomain: z
      .string()
      .min(1, "Subdomain is required")
      .regex(
        /^[a-z0-9-]+$/,
        "Subdomain must only contain lowercase letters, numbers, and hyphens",
      ),
    industry: z.string().optional(),
    size: z.string().optional(),
    subscription: z.string().min(1, "Subscription is required"),
    logo_url: z.string().url().optional().or(z.literal("")),
    ...trialDateFormFields,
  })
  .superRefine(checkTrialDates);

type TenantFormValues = z.infer<typeof tenantFormSchema>;

interface EditTenantDialogProps {
  tenant: TenantRead;
  trigger?: React.ReactNode;
}

export function EditTenantDialog({ tenant, trigger }: EditTenantDialogProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: {
      company: tenant.company,
      subdomain: tenant.subdomain,
      industry: tenant.industry ?? "none",
      size: tenant.size ?? "none",
      subscription: tenant.subscription ?? "free-tier",
      logo_url: tenant.logo_url ?? "",
      trial_start_date: toDateInputValue(tenant.trial_start_date),
      trial_end_date: toDateInputValue(tenant.trial_end_date),
    },
  });

  // Reset form when tenant changes
  useEffect(() => {
    form.reset({
      company: tenant.company,
      subdomain: tenant.subdomain,
      industry: tenant.industry ?? "none",
      size: tenant.size ?? "none",
      subscription: tenant.subscription ?? "free-tier",
      logo_url: tenant.logo_url ?? "",
      trial_start_date: toDateInputValue(tenant.trial_start_date),
      trial_end_date: toDateInputValue(tenant.trial_end_date),
    });
  }, [tenant, form]);

  const [confirmEndTrial, setConfirmEndTrial] = useState(false);

  const refreshTenant = () => {
    invalidateCellTenantQueries(queryClient, tenant.deployment_cell, tenant.id);
    void queryClient.invalidateQueries({
      queryKey: getManagementTenantPreferencesQueryKey({
        ...cellRequestOptions(tenant.deployment_cell),
        path: { tenant_id: tenant.id },
      }),
    });
  };

  const updateTenant = useMutation({
    mutationFn: updateTenantMutation().mutationFn,
    onSuccess: () => {
      refreshTenant();
      setOpen(false);
      toast.success("Tenant updated successfully!");
    },
    onError: (error: unknown) => {
      console.error("Failed to update tenant:", error);
      const errorDetail = getErrorMessage(error);
      toast.error("Failed to update tenant", {
        description: errorDetail,
      });
    },
  });

  const onSubmit = (values: TenantFormValues) => {
    updateTenant.mutate({
      ...cellRequestOptions(tenant.deployment_cell),
      path: { tenant_id: tenant.id },
      body: {
        company: values.company,
        subdomain: values.subdomain,
        industry: values.industry === "none" ? undefined : values.industry,
        size: values.size === "none" ? undefined : values.size,
        // @ts-expect-error - SubscriptionStatus type mismatch with form string value
        subscription: values.subscription || undefined,
        logo_url: values.logo_url ?? undefined,
        trial_start_date: fromDateInputValue(values.trial_start_date),
        trial_end_date: fromDateInputValue(values.trial_end_date),
      },
    });
  };

  const hasTrial = Boolean(tenant.trial_start_date && tenant.trial_end_date);

  const endTrial = useMutation({
    mutationFn: updateTenantMutation().mutationFn,
    onSuccess: () => {
      refreshTenant();
      setOpen(false);
      toast.success("Trial ended", {
        description: `${tenant.company} no longer has a trial.`,
      });
    },
    onError: (error: unknown) => {
      console.error("Failed to end the trial:", error);
      toast.error("Failed to end the trial", {
        description: getErrorMessage(error),
      });
    },
  });

  const onEndTrialConfirmed = () => {
    endTrial.mutate({
      ...cellRequestOptions(tenant.deployment_cell),
      path: { tenant_id: tenant.id },
      body: { trial_start_date: null, trial_end_date: null },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="hover:cursor-pointer">
            <Edit className="mr-1 h-3 w-3" />
            Edit
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-[425px]"
        onEscapeKeyDown={(event) => {
          // The confirmation is a separate Radix layer, and this handler runs
          // first: close only the confirmation and keep the edit form open.
          if (confirmEndTrial) {
            event.preventDefault();
            setConfirmEndTrial(false);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Tenant</DialogTitle>
          <DialogDescription>
            Update the tenant configuration.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit(onSubmit)(e);
            }}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Corporation" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subdomain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subdomain</FormLabel>
                  <FormControl>
                    <Input placeholder="acme" {...field} />
                  </FormControl>
                  <FormDescription>
                    This will be used in URLs like acme.yourapp.com
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="industry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Industry (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an industry" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="technology">Technology</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="manufacturing">
                        Manufacturing
                      </SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Size (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company size" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-1000">
                        201-1000 employees
                      </SelectItem>
                      <SelectItem value="1000+">1000+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subscription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subscription Plan</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select subscription plan" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="free-tier">Free Tier</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <TrialDateFields control={form.control} />

            {hasTrial && (
              <AlertDialog
                open={confirmEndTrial}
                onOpenChange={setConfirmEndTrial}
              >
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={updateTenant.isPending || endTrial.isPending}
                  >
                    {endTrial.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Ending trial...
                      </>
                    ) : (
                      "End trial (converted)"
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      End the trial of {tenant.company}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This removes both trial dates. Use it when the customer
                      converts. Other changes in the edit form are not saved.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onEndTrialConfirmed}>
                      End trial
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateTenant.isPending || endTrial.isPending}
              >
                {updateTenant.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Tenant"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
